import React, { useState, useCallback, useEffect, useRef, useContext, useMemo } from 'react'
import Router from 'next/router'

import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import moment from 'moment'
import { variant } from 'styled-system'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { PencilSimple } from 'phosphor-react'
import { Column, Row } from '../Grid'
import { Text } from '../Text'
import { Select } from '../Select'
import { Calendar } from '../CalendarCartVariant'
import { Button } from '../Button'
import { Modal } from '../Modal'
import { Loader } from '../Loader'
import { CardDefault } from '../Cards'
import { Radio } from '../Radio'
import {
  Chevron,
  ConsultationAdd,
  ConsultationChecked,
  Trash,
  Camera,
  Location,
  BookmarkPrice
} from '../Icons'

import { useFeatureFlags } from 'hooks'
import { sendMultipleScheduling } from 'components/shared/DataLayer'
import TimezoneContext from 'context/timezoneContext'

import { getScheduleBySlug } from 'services/professionalService'
import { getProfile } from 'services/auth'
import { scheduleCartConsultation } from 'services/schedule'
import { getBenefits } from 'services/benefits'

import { formatAppointmentDates, capitalizeFirstLetter } from 'utils/formatters'
import { checkIsMobile } from 'utils/helpers'
import { SCHEDULE_CART_CONSULTATION_ERRORS } from 'utils/constants'

const MultipleConsultationMobile = ({
  consultationsInfo,
  psychologistInfo,
  isModal,
  toggleModal
}) => {
  const [consultationSelected, setConsultationSelected] = useState(false)
  const [selectedTime, setSelectedTime] = useState([])
  const [selectedEdit, setSelectedEdit] = useState('')
  const [modality, setModality] = useState('virtual')
  const [initial, setInitial] = useState(true)
  const [selectedClinic, setSelectedClinic] = useState({})
  const [totalScheduleValue, setTotalScheduleValue] = useState(0)
  const [scheduleCurrency, setScheduleCurrency] = useState('')
  const [schedules, setSchedules] = useState({})
  const [isOpen, setIsOpen] = useState(false)
  const [isNotified, setIsNotified] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDisabledLoading, setIsDisabledLoading] = useState(false)
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState([])
  const [daysQuantity, setDaysQuantity] = useState(moment().daysInMonth())
  const [currentMonth, setCurrentMonth] = useState({
    number: moment().format('MM'),
    name: moment().format('MMMM'),
    year: moment().format('YYYY'),
    fullInitialDate: moment()
      .startOf('month')
      .format('YYYYMM')
  })
  const [dates, setDates] = useState({
    startDate: moment(),
    endDate: moment().endOf('month')
  })
  const [consultationsData, setConsultationsData] = useState([])
  const [displayCalendar, setDisplayCalendar] = useState(false)
  const [selectedDate, setSelectedDate] = useState('')
  const [benefits, setBenefits] = useState([])
  const [showBookmarks, setShowBookmarks] = useState(false)
  const [consultationDays, setConsultationDays] = useState([])
  const [benefitInfo, setBenefitInfo] = useState({
    used: 0,
    total: 0,
    frequency: ''
  })

  const scrollRef = useRef(null)
  const selectPhysicalClinicRef = useRef(null)
  const { frontend_is_outside_benefit_coverage_range_visible } = useFeatureFlags(
    'frontend_is_outside_benefit_coverage_range_visible'
  )
  const userHaveNoCorporateConsultation = selectedTime.find(
    item => item.is_corporate_consultation === false && !consultationsLeft && isUserB2B
  )
  const activeBenefit = benefits?.find(benefit => benefit?.is_active)

  const allConsultationsScheduled = useMemo(
    () =>
      consultationsData.length > 0 &&
      consultationsData.every(consultationsData => consultationsData[0] !== 'schedulable')
  )

  const user = getProfile()
  const isUserB2B = benefits.length > 0

  const consultationsLeft = consultationsInfo.consultationAmount - selectedTime.length
  const firstMonth = moment(dates.startDate).isSame(moment(), 'M')
  const lastMonth =
    moment(dates.endDate).isSameOrAfter(
      moment()
        .subtract(1, 'days')
        .add(3, 'M')
    ) || moment(currentMonth.year + currentMonth.number).diff(moment(), 'M') == 2

  const executeScroll = () => {
    scrollRef.current.scrollIntoView({ behavior: 'smooth' })
  }

  const { timezone } = useContext(TimezoneContext)

  useEffect(() => {
    if (consultationsInfo.consultationAmount === 0) handleAddConsultation()
  }, [consultationsData])

  const isNoConsultationChecked = useCallback(array => {
    let checked = true
    array.map(element => {
      element.indexOf('')
      if (element.indexOf('') !== 1) {
        checked = false
      }
    })
    return checked
  })

  useEffect(() => {
    const consultationDays = consultationsData
      .filter(consultation => consultation[1].split(' ')[0] !== '')
      .map(consultation => consultation[1].split(' ')[0])

    const today = moment().format('YYYY-MM-DD')

    consultationDays.length === 0
      ? setConsultationDays([today])
      : setConsultationDays(consultationDays)
  }, [consultationsData])

  useEffect(() => {
    if (!user) {
      return
    }

    ;(async () => {
      const { data } = await getBenefits(user.id)
      setBenefits(data.content?.contracts)
    })()
  }, [])

  const handleStatusConsultation = useCallback(
    index => {
      setConsultationSelected(true)
      let consultationsDataCopy = consultationsData.slice()
      for (let i = 0; i < consultationsInfo.consultationAmount; i++) {
        consultationsDataCopy[i]?.shift()
        consultationsDataCopy[i]?.unshift('noScheduled') ??
          (consultationsDataCopy[i] = ['noScheduled', '', ''])
        consultationsDataCopy[index]?.shift()
        consultationsDataCopy[index]?.unshift('schedulable') ??
          (consultationsDataCopy[index] = ['schedulable', '', ''])
      }
      setConsultationsData(consultationsDataCopy)
    },
    [consultationsData]
  )

  const indexOfConsultationsNoScheduled = useCallback(array => {
    let consultationsNoScheduled = []
    array.map(element => {
      element.indexOf('')
      if (element.indexOf('') === 1) {
        consultationsNoScheduled.push(array.indexOf(element))
      }
    })
    return consultationsNoScheduled
  })

  const setNextConsultationSchedulable = useCallback(
    index => {
      let consultationsDataCopy = consultationsData.slice()
      let firstNotScheduled = indexOfConsultationsNoScheduled(consultationsDataCopy)[0]
      consultationsDataCopy[index]?.shift()
      consultationsDataCopy[index]?.unshift('noScheduled')

      consultationsDataCopy[firstNotScheduled]?.shift()
      consultationsDataCopy[firstNotScheduled]?.unshift('schedulable')
      setConsultationsData(consultationsDataCopy)
    },
    [consultationsData]
  )

  const indexOfInnerValue = useCallback((array, value, valuePosition) => {
    let index = -1
    array.map(element => {
      element.indexOf(value)
      if (element.indexOf(value) === valuePosition) {
        index = array.indexOf(element)
      }
    })
    return index
  })

  const handleDataConsultation = useCallback((array, value, valuePosition) => {
    let consultationsDataCopy = consultationsData.slice()
    let index = indexOfInnerValue(array, value, valuePosition)
    let repeatedDate = indexOfInnerValue(array, selectedTime[selectedTime.length - 1]?.start, 1)

    if (repeatedDate === -1) {
      consultationsDataCopy[index]?.splice(1, 1, selectedTime[selectedTime.length - 1]?.start)

      consultationsDataCopy[index]?.pop()
      consultationsDataCopy[index]?.push(selectedTime[selectedTime.length - 1]?.id)

      if (!checkIsMobile(993) && index < consultationsData.length) {
        setNextConsultationSchedulable(index)
      } else {
        setConsultationsData(consultationsDataCopy)
      }
    }
  })

  const handleAddConsultation = () => {
    if (!isAllowedToAddMore(consultationsInfo.consultationAmount)) {
      return false
    }

    if (
      consultationsInfo.consultationAmount < 12 &&
      consultationsInfo.consultationAmount !== consultationsInfo.maxSchedules
    ) {
      consultationsInfo.totalValue =
        consultationsInfo.totalValue +
        consultationsInfo.totalValue / consultationsInfo.consultationAmount
      consultationsInfo.consultationAmount = consultationsInfo.consultationAmount + 1

      let consultationsDataCopy = consultationsData.slice()

      if (indexOfInnerValue(consultationsDataCopy, 'schedulable', 0) > -1) {
        consultationsDataCopy.push(['noScheduled', '', ''])
      } else {
        consultationsDataCopy.push(['schedulable', '', ''])
      }

      setConsultationsData(consultationsDataCopy)

      !consultationSelected && setConsultationSelected(true)
    }
  }

  const handleRemoveConsultation = (index = -1) => {
    if (consultationsInfo.consultationAmount < 1) {
      return
    }

    consultationsInfo.totalValue =
      consultationsInfo.totalValue -
      consultationsInfo.totalValue / consultationsInfo.consultationAmount

    if (index >= 0) {
      handleUnmarkHour(index)
    }

    let consultationsDataCopy = consultationsData.slice()
    consultationsDataCopy.splice(index, 1)

    if (consultationsDataCopy.length > 0) {
      consultationsInfo.consultationAmount = consultationsInfo.consultationAmount - 1
    } else {
      setIsFeedbackOpen(false)
      setConsultationSelected(false)
    }

    setConsultationsData(consultationsDataCopy)
  }

  const confirmScheduling = () => {
    const index = indexOfInnerValue(consultationsData, 'schedulable', 0)
    setNextConsultationSchedulable(index)
    setDisplayCalendar(false)
  }

  const hasHourSelected = () => {
    const index = indexOfInnerValue(consultationsData, 'schedulable', 0)
    return consultationsData[index] !== undefined && consultationsData[index]?.[1] === ''
      ? true
      : false
  }

  const showConsultationDate = consultationDate => {
    return moment(consultationDate).format('DD/MM/YYYY')
  }

  const showConsultationDuration = consultationTime => {
    return (
      moment(consultationTime).format('HH:mm') +
      ' - ' +
      moment(consultationTime)
        .add(50, 'minute')
        .format('HH:mm')
    )
  }

  useEffect(() => {
    const availableFrequency = ['daily', 'weekly', 'monthly', 'all_time'].find(frequency => {
      const { total, used } = activeBenefit?.uses?.[frequency] || {}
      return total > 0 && used < total
    })
    if (availableFrequency) {
      setBenefitInfo({ ...activeBenefit.uses[availableFrequency], frequency: availableFrequency })
    }
  }, [activeBenefit])

  useEffect(() => {
    benefitInfo.total - benefitInfo.used < consultationsData.length &&
    isUserB2B &&
    activeBenefit?.uses !== null
      ? setShowBookmarks(true)
      : setShowBookmarks(false)
  }, [benefitInfo, consultationsData, activeBenefit])

  useEffect(() => {
    if (selectedTime.length > 0) {
      executeScroll()
      handleDataConsultation(consultationsData, 'schedulable', 0)
    }
  }, [selectedTime, scrollRef])

  const handlePreviousMonth = () => {
    const isBeforeCurrentDate = moment(dates.startDate)
      .subtract(1, 'M')
      .isBefore(moment(), 'day')

    setCurrentMonth({
      number: moment(dates.startDate)
        .subtract(1, 'M')
        .format('MM'),
      name: moment(dates.startDate)
        .subtract(1, 'M')
        .format('MMMM'),
      year:
        currentMonth.number === '12'
          ? moment(dates.startDate)
              .subtract(1, 'year')
              .format('YYYY')
          : currentMonth.year,
      fullInitialDate: moment(dates.startDate)
        .subtract(1, 'M')
        .format('YYYYMMDD')
    })

    setDates({
      startDate: isBeforeCurrentDate
        ? moment()
        : moment(currentMonth.fullInitialDate)
            .subtract(1, 'M')
            .startOf('month'),
      endDate: moment(currentMonth.fullInitialDate)
        .subtract(1, 'M')
        .endOf('month')
    })
  }

  const handleNextMonth = () => {
    setCurrentMonth({
      number: moment(dates.startDate)
        .add(1, 'M')
        .format('MM'),
      name: moment(dates.startDate)
        .add(1, 'M')
        .format('MMMM'),
      year:
        currentMonth.number === '12'
          ? moment(dates.startDate)
              .add(1, 'year')
              .format('YYYY')
          : currentMonth.year,
      fullInitialDate: moment(dates.startDate)
        .add(1, 'M')
        .format('YYYYMMDD')
    })

    setDates({
      startDate: moment(currentMonth.fullInitialDate)
        .add(1, 'M')
        .startOf('month'),
      endDate: moment(currentMonth.fullInitialDate)
        .add(1, 'M')
        .endOf('month')
    })
  }

  const handleRemoveStatusConsultation = (value, index) => {
    const isId = value.lengh > 2 ? true : false
    let indexConsultationId = ''

    if (index > -1) {
      selectedTime.splice(index, 1)
      getScheduleMutation.mutate()
    }

    let consultationsDataCopy = consultationsData.slice()

    if (isId) {
      indexConsultationId = indexOfInnerValue(consultationsDataCopy, value, 2)
    } else {
      indexConsultationId = indexOfInnerValue(consultationsDataCopy, index, 2)
    }

    let indexConsultationSchedulable = indexOfInnerValue(consultationsData, 'schedulable', 0)

    if (indexConsultationId === indexConsultationSchedulable) {
      if (isId) {
        consultationsDataCopy[indexConsultationId][0] = 'noScheduled'
        consultationsDataCopy[indexConsultationId][1] = ''
        consultationsDataCopy[indexConsultationId][2] = ''
      } else {
        consultationsDataCopy[indexConsultationId] = ['noScheduled', '', '']
      }

      let firstNotScheduled = indexOfConsultationsNoScheduled(consultationsDataCopy)[0]

      consultationsDataCopy[firstNotScheduled]?.shift()
      consultationsDataCopy[firstNotScheduled]?.unshift('schedulable')
    }

    setConsultationsData(consultationsDataCopy)
  }

  const handleUnmarkHour = selectedHourIndex => {
    const selectedHourId = consultationsData[selectedHourIndex][2]
    const index = selectedTime.findIndex(schedule => schedule.id === selectedHourId)
    handleRemoveStatusConsultation(selectedHourIndex, index)
  }

  const handleDelete = id => {
    const index = selectedTime.findIndex(schedule => schedule.id === id)
    handleRemoveStatusConsultation(id, index)
  }

  const setConsultationVariant = index => {
    let variant = ''
    if (index > 0) {
      variant = 'consultation-disabled'
    }

    if (
      consultationsData[index] !== undefined &&
      consultationsData[index]?.[0] === 'noScheduled' &&
      consultationsData[index]?.[1] === ''
    ) {
      variant = 'consultation-disabled'
    }

    if (consultationsData[index] !== undefined && consultationsData[index]?.[0] === 'schedulable') {
      variant = 'consultation-selected'
    }

    if (
      consultationsData[index] !== undefined &&
      consultationsData[index]?.[0] === 'noScheduled' &&
      consultationsData[index]?.[1] !== ''
    ) {
      variant = ''
    }
    return variant
  }

  const handleEdit = index => {
    setIsFeedbackOpen(false)
    setSelectedEdit(consultationsData[index][2])
    setSelectedMonth(capitalizeFirstLetter(moment(consultationsData[index][1]).format('MMMM YYYY')))

    setDates({
      startDate: moment(consultationsData[index][1]).format('YYYYMMDD'),
      endDate: moment(consultationsData[index][1])
        .add(6, 'days')
        .format('YYYYMMDD')
    })

    setCurrentMonth({
      number: moment(consultationsData[index][1]).format('MM'),
      name: moment(consultationsData[index][1]).format('MMMM'),
      year: moment(consultationsData[index][1]).format('YYYY'),
      fullInitialDate: moment(consultationsData[index][1])
        .startOf('month')
        .format('YYYYMMDD')
    })
  }

  const { register, watch, setValue } = useForm()
  const value = watch(['timezone', 'clinics'])

  const handleMultipleConsultation = async () => {
    setIsOpen(false)
    try {
      setIsLoading(true)
      sessionStorage.removeItem('scheduleInfo')
      const data = {
        amount: Number(consultationsInfo.consultationAmount),
        schedule_id: selectedClinic.id,
        consultations: selectedTime.map(item => ({
          professional_id: psychologistInfo.id,
          start: moment(item.start).format('X'),
          date: item.date,
          time: item.time,
          timezone: timezone,
          schedule_id: item.schedule_id,
          available_teleconf: Boolean(psychologistInfo.clinics.virtual)
        }))
      }
      const { data: checkoutDetails } = await scheduleCartConsultation(user?.id, data)

      sendMultipleScheduling({
        value: checkoutDetails?.content.checkout?.total_value,
        items: checkoutDetails?.content?.consultations_success?.map(consultation => ({
          item_id: consultation?.professional?.id,
          item_name: consultation?.professional?.name,
          price: consultation?.ocurrence?.price?.value
        }))
      })

      localStorage.setItem('checkoutData', JSON.stringify(checkoutDetails))

      Router.push(
        `/agendar-consultas/finalizar-pagamento?checkoutId=${checkoutDetails?.content?.checkout_id}&timezone=${checkoutDetails?.content?.consultations_success[0]?.occurrence?.timezone}&newCheckout=generateNewCheckout`
      )
    } catch (error) {
      const errorCode = error?.response?.data?.log_content?.error_code

      toast.error(
        SCHEDULE_CART_CONSULTATION_ERRORS?.[errorCode] || SCHEDULE_CART_CONSULTATION_ERRORS.default,
        {
          autoClose: 5000
        }
      )

      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  const closeMultipleScheduling = () => {
    if (isModal) {
      return toggleModal(psychologistInfo)
    }

    return Router.back()
  }

  useEffect(() => {
    setTotalScheduleValue(selectedClinic?.price?.value * consultationsInfo.consultationAmount)
  }, [consultationsInfo.consultationAmount])

  useEffect(() => {
    handleModality()
  }, [modality, value?.clinics])

  const handleModality = () => {
    let selectedSchedule
    let totalValue
    if (modality === 'offline') {
      selectedSchedule =
        psychologistInfo?.clinics?.physical?.find(clinic => clinic.id === value.clinics) ??
        psychologistInfo?.clinics?.physical[0]
    } else {
      selectedSchedule = psychologistInfo?.clinics?.virtual
    }
    totalValue = selectedSchedule.price.value * consultationsInfo.consultationAmount

    setSelectedClinic(selectedSchedule)
    setTotalScheduleValue(totalValue)
    setScheduleCurrency(selectedSchedule.price.currency)
  }

  const isAllowedToAddMore = () => {
    if (
      selectedTime?.length >= consultationsInfo.consultationAmount &&
      consultationsInfo.consultationAmount < 12
    ) {
      return true
    }

    return false
  }

  const queryClient = useQueryClient()

  const handleScheduleResponse = (scheduleResponse, isNewResponse) => {
    const days = moment(dates.endDate).diff(moment(dates.startDate), 'days') + 1
    setDaysQuantity(days)
    const appointments = formatAppointmentDates(scheduleResponse.dates, scheduleResponse.schedule)

    setSchedules({
      schedule: scheduleResponse.schedule,
      appointments: appointments.splice(0, days)
    })

    if (isNewResponse) {
      // Se for uma response nova, armazena os dados no cache
      queryClient.setQueryData(
        ['getScheduleMutation', `start-${dates.startDate}-end-${dates.endDate}`],
        {
          data: scheduleResponse,
          timestamp: Date.now()
        }
      )
    }
  }

  const handleScheduleRequest = async () => {
    const cacheKey = [
      'getScheduleMutation',
      `start-${dates.startDate}-end-${dates.endDate}`,
      `clinicId-${selectedClinic.id}`
    ]

    const cachedData = queryClient.getQueryData(cacheKey)

    const staleTime = 1000 * 45

    // Verifica se os dados existem no cache antes de acessar o timestamp
    if (cachedData) {
      const isStale = Date.now() - cachedData.timestamp > staleTime

      // Se os dados não estiverem obsoletos, usa o cache
      if (!isStale) {
        return { data: cachedData.data, isNewResponse: false }
      }
    }

    return {
      data: await getScheduleBySlug(
        psychologistInfo.slug,
        timezone,
        dates.startDate.format('YYYYMMDD'),
        dates.endDate.format('YYYYMMDD'),
        modality === 'virtual',
        selectedClinic.id,
        null,
        null,
        consultationDays
      ),
      isNewResponse: true
    }
  }

  const getScheduleMutation = useMutation({
    mutationFn: () => handleScheduleRequest(),
    onSuccess: ({ data, isNewResponse }) => {
      handleScheduleResponse(data, isNewResponse)
    },
    onError: error => {
      console.log(error)
    }
  })

  useEffect(() => {
    getScheduleMutation.mutate()
  }, [dates, timezone, selectedClinic])

  return (
    <Row flexDirection='column' width='100%' px='16px'>
      <Column xs='12' mb='3'>
        <Text textAlign='left' variant='bigger'>
          Agende sua consulta
        </Text>
      </Column>
      <Column xs='12'>
        <Column
          justifyContent='space-between'
          boxShadow='boxShadow'
          borderRadius='4px'
          width='100%'
          px='14px'
          py='16px'
          mb='24px'
        >
          <Text alignItems='left' color='black' variant='small'>
            {psychologistInfo.title} <Bold>{psychologistInfo.name}</Bold>
          </Text>
          {!user?.contract?.features?.hide_consultation_value && !displayCalendar && (
            <Column>
              <Text textAlign='left' color='black' py='8px' variant='small'>
                Valor da consulta:{' '}
                <Text as='span' fontWeight='bold' color='black' variant='small'>
                  {`${scheduleCurrency} ${selectedClinic?.price?.value}`}
                </Text>
              </Text>
              {benefits.length === 0 && (
                <Text textAlign='left' color='black' variant='small'>
                  Total:{' '}
                  <Text as='span' fontWeight='bold' color='black' variant='small'>
                    {`${scheduleCurrency} ${totalScheduleValue}`}
                  </Text>
                </Text>
              )}
            </Column>
          )}
        </Column>
        <Column alignItems='flex-start'>
          <Text variant='small' mb='24px'>
            Atendimento:
          </Text>
          <Row mb='16px'>
            <Radio
              initial={initial}
              checked={initial || modality === 'virtual'}
              selected={modality === 'virtual'}
              onClick={() => {
                setInitial(false)
                setModality('virtual')
              }}
              name='options'
              label='Online'
              value={1}
              icon={<Camera size='16' color='black' />}
              mr='16px'
              disabled={!isNoConsultationChecked(consultationsData)}
            />
            <Radio
              initial={false}
              disabled={
                (typeof user?.contract?.features !== 'undefined' &&
                  !user?.contract?.features?.presential_consultation_enabled) ||
                !psychologistInfo.clinics?.physical.length ||
                !isNoConsultationChecked(consultationsData)
              }
              checked={modality === 'offline'}
              selected={modality === 'offline'}
              onClick={() => {
                setInitial(false)
                setModality('offline')
              }}
              name='options'
              label='Presencial'
              value={2}
              icon={
                <Location
                  size='16'
                  color={!psychologistInfo.clinics?.physical.length ? '#7F8C8D' : 'black'}
                />
              }
            />
          </Row>
          {Boolean(psychologistInfo?.clinics?.physical?.length) && modality === 'offline' && (
            <Row width={340}>
              <Select
                ref={selectPhysicalClinicRef}
                register={register}
                onChange={e => {
                  setValue('clinics', e.target.value)
                }}
                value={value.clinics || psychologistInfo.clinics.formatedPhysicalClinics?.[0].value}
                label='Selecione uma opção'
                placeholder='Selecione uma opção'
                variant='primary'
                options={psychologistInfo.clinics.formatedPhysicalClinics}
                name='clinics'
                disabled={!isNoConsultationChecked(consultationsData)}
              />
            </Row>
          )}
        </Column>
        {frontend_is_outside_benefit_coverage_range_visible && isUserB2B && showBookmarks && (
          <Row justifyContent='flex-end' mb='16px'>
            <Column>
              <BookmarkPrice size='32px' color='#FFA938' />
            </Column>
            <Column maxWidth='360px'>
              <Text variant='tiny' color='gray.n350' textAlign='left'>
                Consultas adicionais serão cobradas de forma integral ou parcial, pois o limite do
                seu benefício foi atingido
              </Text>
            </Column>
          </Row>
        )}
        <Row justifyContent='space-between'>
          <Row width='100%'>
            <Column width='100%' mt='15px'>
              <Column width='100%' display={displayCalendar ? 'none' : 'block'}>
                <Column
                  boxShadow='boxShadow'
                  mb='16px'
                  borderRadius='4px'
                  maxHeight='360px'
                  overflowY='auto'
                >
                  {[...Array(consultationsInfo.consultationAmount)].map((consultation, index) => (
                    <CardContainer id='CardContainer'>
                      {frontend_is_outside_benefit_coverage_range_visible &&
                        showBookmarks &&
                        selectedTime[index]?.is_corporate_consultation === false && (
                          <Row position='absolute' top='-4px' left='4px'>
                            <BookmarkPrice size='30px' color='#FFA938' />
                          </Row>
                        )}
                      <Card id='Card' key={index} variant={setConsultationVariant(index)}>
                        <Text textAlign='left' color='#000'>
                          Consulta {index + 1 + ' '}
                          {consultationsData[index] !== undefined &&
                            consultationsData[index]?.[1] &&
                            consultationsData[index]?.[0] !== 'schedulable' && (
                              <ConsultationChecked color='#27ae60' width='14px' height='12px' />
                            )}
                        </Text>

                        <Row justifyContent='space-between' id='ALL_CONTENT_ROW'>
                          {consultationsData[index] !== undefined &&
                          consultationsData[index]?.[0] === 'schedulable' ? (
                            <>
                              {consultationsData[index] !== undefined &&
                              consultationsData[index]?.[1] ? (
                                <Row id='AAAAAAAAAAAAAAAA'>
                                  <CardConsultationLabel>
                                    {showConsultationDate(consultationsData[index]?.[1])}
                                  </CardConsultationLabel>
                                  <CardConsultationLabel>
                                    {showConsultationDuration(consultationsData[index]?.[1])}
                                  </CardConsultationLabel>

                                  {user?.contract?.features?.allow_contract_use_exceed !==
                                    false && (
                                    <CardConsultationIcon
                                      variant='clickable'
                                      onClick={() => {
                                        handleStatusConsultation(index)
                                        handleEdit(index)
                                        setDisplayCalendar(true)
                                        window.scrollTo(0, 0)
                                      }}
                                    >
                                      <PencilSimple color='#FF7708' fontSize={18} />
                                    </CardConsultationIcon>
                                  )}
                                </Row>
                              ) : (
                                <>
                                  <CardConsultationLabel
                                    variant={
                                      setConsultationVariant(index) === 'consultation-disabled'
                                        ? 'disabled'
                                        : 'clickable'
                                    }
                                    onClick={() => {
                                      handleStatusConsultation(index)
                                      setDisplayCalendar(true)
                                      window.scrollTo(0, 0)
                                    }}
                                    my='auto'
                                  >
                                    Escolher data e horário
                                  </CardConsultationLabel>
                                  <CardConsultationIcon
                                    onClick={() => handleRemoveConsultation()}
                                    variant={
                                      consultationsInfo.consultationAmount > 1
                                        ? 'clickable'
                                        : 'disable'
                                    }
                                  >
                                    <Trash
                                      color={
                                        consultationsInfo.consultationAmount > 0
                                          ? '#FF7708'
                                          : '#BDC3C7'
                                      }
                                    />
                                  </CardConsultationIcon>
                                </>
                              )}
                            </>
                          ) : (
                            <>
                              {consultationsData[index] !== undefined &&
                              consultationsData[index]?.[1] ? (
                                <Row justifyContent='space-between' gridGap='4px' width='100%'>
                                  <Column gridGap='2px'>
                                    <Row gridGap='8px'>
                                      {activeBenefit?.uses !== null && (
                                        <CardConsultationLabel>
                                          {selectedTime?.[index]?.is_corporate_consultation
                                            ? 'Corporativa'
                                            : 'Particular'}
                                        </CardConsultationLabel>
                                      )}
                                      <CardConsultationLabel>
                                        {showConsultationDate(consultationsData[index]?.[1])}
                                      </CardConsultationLabel>
                                    </Row>
                                    <CardConsultationLabel>
                                      {showConsultationDuration(consultationsData[index]?.[1])}
                                    </CardConsultationLabel>
                                  </Column>

                                  <Row gridGap='8px'>
                                    {user?.contract?.features?.allow_contract_use_exceed !==
                                      false && (
                                      <CardConsultationIcon
                                        variant='clickable'
                                        onClick={() => {
                                          handleStatusConsultation(index)
                                          handleEdit(index)
                                          setDisplayCalendar(true)
                                          window.scrollTo(0, 0)
                                        }}
                                      >
                                        <PencilSimple color='#FF7708' fontSize={18} />
                                      </CardConsultationIcon>
                                    )}

                                    <CardConsultationIcon
                                      variant='clickable'
                                      onClick={() => handleRemoveConsultation(index)}
                                    >
                                      <Trash
                                        color={
                                          consultationsInfo.consultationAmount > 0
                                            ? '#FF7708'
                                            : '#BDC3C7'
                                        }
                                      />
                                    </CardConsultationIcon>
                                  </Row>
                                </Row>
                              ) : (
                                <>
                                  <CardConsultationLabel
                                    variant={
                                      setConsultationVariant(index) === 'consultation-disabled'
                                        ? 'disabled'
                                        : 'clickable'
                                    }
                                    onClick={() => {
                                      handleStatusConsultation(index)
                                      setDisplayCalendar(true)
                                      window.scrollTo(0, 0)
                                    }}
                                    my='auto'
                                  >
                                    Escolher data e horário
                                  </CardConsultationLabel>
                                  {consultationsData[index]?.[0] !== 'schedulable' && (
                                    <CardConsultationIcon
                                      onClick={() => handleRemoveConsultation()}
                                      variant='disable'
                                    >
                                      <Trash
                                        color={
                                          consultationsInfo.consultationAmount > 1
                                            ? '#FF7708'
                                            : '#BDC3C7'
                                        }
                                      />
                                    </CardConsultationIcon>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Row>
                      </Card>
                    </CardContainer>
                  ))}
                </Column>
                <AddConsultation>
                  <ConsultationAddIcon
                    color={
                      isAllowedToAddMore(consultationsInfo.consultationAmount)
                        ? '#FF7708'
                        : '#BDC3C7'
                    }
                  />
                  <CardConsultationLabel
                    variant={
                      isAllowedToAddMore(consultationsInfo.consultationAmount)
                        ? 'clickable'
                        : 'disable'
                    }
                    style={{ marginLeft: '10px' }}
                    onClick={() => {
                      handleAddConsultation()
                      getScheduleMutation.mutate()
                    }}
                  >
                    Adicionar mais consultas
                  </CardConsultationLabel>
                </AddConsultation>
              </Column>
              {displayCalendar && (
                <Column>
                  <Row justifyContent='center' height='50px' alignItems='center'>
                    <Chevron
                      variant='right'
                      display={firstMonth ? 'none' : 'block'}
                      cursor='pointer'
                      onClick={handlePreviousMonth}
                    />
                    <Text mx='15px' color='primary' textTransform='capitalize'>
                      {`${currentMonth.name} ${currentMonth.year}`}
                    </Text>
                    <Chevron
                      variant='left'
                      display={lastMonth ? 'none' : 'block'}
                      cursor='pointer'
                      onClick={handleNextMonth}
                    />
                  </Row>
                  <CalendarContainer ref={scrollRef} overflowX='auto'>
                    {(getScheduleMutation.isLoading || isDisabledLoading) && (
                      <Row
                        width='100%'
                        height='100%'
                        alignItems='center'
                        background='white'
                        justifyContent='center'
                      >
                        <Loader size={60} />
                      </Row>
                    )}
                    <Row width='100%' mt='15px'>
                      <Row
                        display={
                          getScheduleMutation.isLoading || isDisabledLoading ? 'none' : 'block'
                        }
                      >
                        <Calendar
                          id='calendar-id'
                          consultationsData={consultationsData}
                          className='calendar-class'
                          showWarnings={true}
                          consultationAmount={consultationsInfo.consultationAmount}
                          selectedTime={time => setSelectedTime(time)}
                          schedules={schedules}
                          handleDelete={handleDelete}
                          daysQuantity={daysQuantity}
                          selectedEdit={selectedEdit}
                          setSelectedEdit={setSelectedEdit}
                          mobileSelect={true}
                          setSelectedDate={setSelectedDate}
                          setIsDisabledLoading={setIsDisabledLoading}
                          handleRemoveConsultation={handleRemoveConsultation}
                          psychologistInfo={psychologistInfo}
                          showBookmarks={showBookmarks}
                          benefitInfo={benefitInfo}
                        />
                      </Row>
                    </Row>
                  </CalendarContainer>

                  <ConfirmSchedulingButton
                    onClick={() => {
                      confirmScheduling()
                      hasHourSelected()
                    }}
                    disabled={isLoading || !selectedTime?.length}
                    type='submit'
                  >
                    <Text variant='small' fontWeight='bold'>
                      {isLoading ? <Loader size={30} /> : 'Confirmar seleção'}
                    </Text>
                  </ConfirmSchedulingButton>
                </Column>
              )}
            </Column>
          </Row>
        </Row>
        {!displayCalendar && (
          <Column>
            <Row justifyContent='flex-end' mt={20}>
              <Button
                height={48}
                width={163}
                variant='primary-outlined'
                onClick={() => closeMultipleScheduling()}
                mr={18}
              >
                <Text variant='small' fontWeight='bold'>
                  Voltar
                </Text>
              </Button>
              <Button
                onClick={() =>
                  (selectedTime?.length !== consultationsInfo.consultationAmount && !isNotified) ||
                  userHaveNoCorporateConsultation
                    ? setIsOpen(true)
                    : handleMultipleConsultation()
                }
                disabled={isLoading || !allConsultationsScheduled}
                height={48}
                width={163}
                type='submit'
              >
                <Text variant='tiny' fontWeight='bold'>
                  {isLoading ? <Loader size={30} /> : 'Ir para o pagamento'}
                </Text>
              </Button>
            </Row>
          </Column>
        )}
      </Column>
      <Modal
        shouldCloseOnEscape={false}
        shouldCloseOnClickOutside={false}
        isOpened={isOpen}
        setIsOpened={setIsOpen}
      >
        {({ innerModalRef }) => (
          <Column
            ref={innerModalRef}
            bg='white'
            borderRadius='8px'
            boxShadow='0px 2px 12px #00000040'
            px={32}
            py={21}
            width={366}
          >
            <Text mb={25} fontWeight='bold'>
              Atenção!
            </Text>
            <Column>
              <Text color='gray.n500' variant='small' fontWeight='600'>
                {userHaveNoCorporateConsultation ? (
                  <>
                    As datas escolhidas{' '}
                    <Text color='gray.n500' variant='small' fontWeight='600' as='span'>
                      excedem o limite de consultas
                    </Text>{' '}
                    que fazem parte do seu benefício corporativo!
                  </>
                ) : (
                  <>
                    Você ainda tem {consultationsLeft}{' '}
                    {consultationsLeft > 1 ? 'consultas ' : 'consulta '}
                    para agendar!
                  </>
                )}
              </Text>
              <Text color='gray.n500' variant='small'>
                {userHaveNoCorporateConsultation ? (
                  <>
                    Você pode retornar para o agendamento e escolher uma outra data que{' '}
                    <Text color='gray.n500' variant='small' fontWeight='600' as='span'>
                      estará incluso dentro do seu benefício{' '}
                    </Text>
                    ou caso queira continuar para o pagamento, as consultas serão{' '}
                    <Text as='span' color='gray.n500' variant='small' fontWeight='600'>
                      cobradas fora do desconto de benefício.
                    </Text>
                    .
                  </>
                ) : (
                  <>Por favor, selecione uma data e horário para as consultas agendadas.</>
                )}
              </Text>
            </Column>
            <Button mt='20px' height='48px' variant='primary' onClick={() => setIsOpen(false)}>
              Voltar para o agendamento
            </Button>
            {userHaveNoCorporateConsultation && (
              <Button
                mt='10px'
                height='48px'
                variant='primary-outlined'
                disabled={
                  isLoading || selectedTime?.length !== consultationsInfo.consultationAmount
                }
                onClick={() => handleMultipleConsultation()}
              >
                <Text variant='small' fontWeight='bold'>
                  {isLoading ? <Loader size={30} /> : 'Seguir para o pagamento'}
                </Text>
              </Button>
            )}
          </Column>
        )}
      </Modal>
    </Row>
  )
}

const CardContainer = styled(Column)`
  position: relative;
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.15);

  &:last-child {
    border-bottom: none;
  }
`

const Card = styled(CardDefault)(
  () => `
  backgroud: #ffffff;
  width: 100%;
  border-radius: 0px;
  padding: 16px 20px 16px 32px;
  cursor: pointer;
`,
  variant({
    variants: {
      'consultation-selected': {
        borderLeft: '4px solid #FF7708',
        background: '#fff7f6 0% 0% no-repeat padding-box'
      },
      'consultation-disabled': {
        background: '#f8f8f8'
      },
      'consultation-scheduled': {
        background: '#ffffff'
      }
    }
  })
)

const CardConsultationLabel = styled(CardDefault)(
  () => `
  text-align: left;
  padding: 0;
  font-size: 14px;
  background: transparent;
  cursor: pointer;
`,
  variant({
    variants: {
      clickable: {
        color: 'primary',
        textDecoration: 'underline'
      },
      disable: {
        color: 'gray.n300',
        textDecoration: 'none'
      },
      normal: {
        color: '#7F8C8D'
      }
    }
  })
)

const CardConsultationIcon = styled(CardConsultationLabel)`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const ConsultationAddIcon = styled(ConsultationAdd)`
  margin: 15px;
`

const AddConsultation = styled(CardDefault)(
  () => `
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 0;
`,
  variant({
    variants: {
      'consultation-selected': {
        borderLeft: '4px solid #FF7708'
      }
    }
  })
)

const CalendarContainer = styled(CardDefault)`
  ${({ theme: { shadows } }) => `
    box-shadow: ${shadows.boxShadow};
    border-radius: 4px;
    padding: 5px;
    height: 350px;
    justify-content: center;
  `}
`

const ConfirmSchedulingButton = styled(Button)`
  width: 100%;
  height: 48px;
  margin-top: 12px;
`

const Bold = styled.span`
  font-weight: bold;
  color: black;
`

export default MultipleConsultationMobile
